import React, { useEffect, useRef } from 'react';

interface WordCloudProps {
  words: Array<{ text: string; value: number }>;
  width?: number;
  height?: number;
}

const WordCloud: React.FC<WordCloudProps> = ({ words, width = 600, height = 400 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || words.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Sort words by value (descending)
    const sortedWords = [...words].sort((a, b) => b.value - a.value).slice(0, 50);

    // Calculate font size range
    const maxValue = Math.max(...sortedWords.map(w => w.value));
    const minValue = Math.min(...sortedWords.map(w => w.value));
    const fontSizeRange = { min: 12, max: 48 };

    // Colors for different word sizes
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

    // Place words on canvas
    const placedWords: Array<{ x: number; y: number; width: number; height: number }> = [];

    sortedWords.forEach((word, index) => {
      const fontSize = fontSizeRange.min + 
        ((word.value - minValue) / (maxValue - minValue || 1)) * 
        (fontSizeRange.max - fontSizeRange.min);
      
      ctx.font = `bold ${fontSize}px Arial`;
      const metrics = ctx.measureText(word.text);
      const textWidth = metrics.width;
      const textHeight = fontSize;

      // Try to place word without overlapping
      let placed = false;
      let attempts = 0;
      const maxAttempts = 100;

      while (!placed && attempts < maxAttempts) {
        const x = Math.random() * (width - textWidth);
        const y = Math.random() * (height - textHeight) + textHeight;

        // Check collision
        const collides = placedWords.some(placed => {
          return !(
            x + textWidth < placed.x ||
            x > placed.x + placed.width ||
            y + textHeight < placed.y ||
            y > placed.y + placed.height
          );
        });

        if (!collides) {
          // Draw word
          const color = colors[index % colors.length];
          ctx.fillStyle = color;
          ctx.fillText(word.text, x, y);
          
          placedWords.push({ x, y, width: textWidth, height: textHeight });
          placed = true;
        }
        attempts++;
      }
    });
  }, [words, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="w-full h-full"
      style={{ maxWidth: '100%', height: 'auto' }}
    />
  );
};

export default WordCloud;

